// ═══════════════════════════════════════════════════════════════
// twa.js — Trusted Web Activity (Android wrapper) context detection
//
// Two distinct signals, both captured into sessionStorage at first
// load so they survive in-app SPA navigation AND the URL-cleanup
// the existing post-Stripe handler does in App.jsx:
//
//   ?ctx=android-twa  → we ARE in the TWA right now. Set by the
//                       Bubblewrap-generated TWA launcher. Drives
//                       all "hide subscription UI" gating.
//
//   ?src=android-twa  → we came FROM the TWA but are currently in
//                       a regular browser (user tapped "Open in
//                       Browser" from the TWA to sign up). Drives
//                       the "return to app" view after Stripe success.
//
// Do NOT use display-mode: standalone or User-Agent heuristics —
// those false-positive on desktop PWAs, iOS PWAs, regular Android
// Chrome PWAs, and in-app browsers.
// ═══════════════════════════════════════════════════════════════

const CTX_KEY = 'twa-context';      // we ARE in the TWA
const SRC_KEY = 'twa-from-context'; // we came FROM the TWA (now in browser)
const ANDROID_TWA_VALUE = 'android';

let cachedCtx = null;
let cachedSrc = null;

function readSession(key) {
  try {
    return typeof window !== 'undefined' && window.sessionStorage
      ? window.sessionStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
}

function writeSession(key, value) {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem(key, value);
    }
  } catch {
    // sessionStorage unavailable (private mode) — flag won't persist this session
  }
}

// Read ctx/src query params at first load and persist if they indicate
// Android TWA origin. Idempotent. Reads BEFORE App.jsx cleans Stripe
// params so the src flag survives.
function captureContextFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ctx') === 'android-twa') {
      writeSession(CTX_KEY, ANDROID_TWA_VALUE);
      cachedCtx = true;
    }
    if (params.get('src') === 'android-twa') {
      writeSession(SRC_KEY, ANDROID_TWA_VALUE);
      cachedSrc = true;
    }
  } catch {
    // URL parsing failed — ignore
  }
}

// Returns true if the app is running INSIDE the Android TWA wrapper.
// Web users (including desktop PWAs and iOS PWAs) always return false.
function isAndroidWrapper() {
  if (cachedCtx !== null) return cachedCtx;
  cachedCtx = readSession(CTX_KEY) === ANDROID_TWA_VALUE;
  return cachedCtx;
}

// Returns true if the current session originated from the Android TWA
// (i.e., we're in Chrome on the same device after the user tapped
// "Open in Browser" to sign up). Persisted in sessionStorage so it
// survives the post-Stripe URL cleanup in App.jsx.
function isFromAndroidTwa() {
  if (cachedSrc !== null) return cachedSrc;
  cachedSrc = readSession(SRC_KEY) === ANDROID_TWA_VALUE;
  return cachedSrc;
}

// Test-only setters. Lets unit tests force flags without touching sessionStorage.
function __setTwaContextForTests(value) {
  cachedCtx = value === true ? true : value === false ? false : null;
}
function __setTwaFromContextForTests(value) {
  cachedSrc = value === true ? true : value === false ? false : null;
}

// Capture on module load so the flags are ready before any component mounts
// AND before App.jsx cleans Stripe URL params.
captureContextFromUrl();

export {
  isAndroidWrapper,
  isFromAndroidTwa,
  captureContextFromUrl,
  __setTwaContextForTests,
  __setTwaFromContextForTests,
};
